import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import formatMessage from 'format-message'
import { Tooltip, OverlayTrigger, Button } from 'react-bootstrap'
import { monitorNarrateElementReadiness } from '../utils/narrateElementLoader'

// Tools that need a book page and are shown greyed out on the title page (context="coverpage"):
// highlighting has no text to mark, and the notebook/AI drawers are keyed to the current page id.
export const COVERPAGE_DISABLED_TOOLS = [
  'highlighting',
  'notebook',
  'quickreview',
  'assistant',
]

export const IconBar = props => {
  const hasUser = !!(props.userEmail && String(props.userEmail).trim())
  const isCoverpage = props.context === 'coverpage'
  // one answer for every path that can open a drawer (click, localStorage restore, events)
  const isDrawerDisabled = id =>
    ((id === 'assistant' || id === 'quickreview') &&
      props.assistantEnabled === false) ||
    (isCoverpage && COVERPAGE_DISABLED_TOOLS.includes(id))
  const disabledTooltip = formatMessage('Not available on the title page')
  const [, setActiveIcon] = useState(null)
  const [activeDrawer, setActiveDrawer] = useState(null)

  const [, setAssistantOpen] = useState(
    typeof document !== 'undefined' &&
      document.body.classList.contains('has-chat-drawer')
  )
  const [, setGlossaryOpen] = useState(
    !!(
      typeof document !== 'undefined' &&
      document.getElementById('dialog_view_glossary')
    )
  )
  const [, setSettingsOpen] = useState(
    !!(
      typeof document !== 'undefined' &&
      document.getElementById('dialog_advanced_options')
    )
  )

  const [highlightOn, setHighlightOn] = useState(() => {
    try {
      const val = localStorage.getItem('highlight_on')
      if (val !== null) return JSON.parse(val)
    } catch (e) {
      // Fall back to jStorage below.
    }
    if (typeof $ !== 'undefined' && $.jStorage) {
      return $.jStorage.get('highlighter-on') || false
    }
    return false
  })

  const [narrateElem, setNarrateElem] = useState(null)
  const [narrateVisible, setNarrateVisible] = useState(false)

  const [authorToolOn, setAuthorToolOn] = useState(() => {
    if (
      typeof $ !== 'undefined' &&
      $.jStorage &&
      $.jStorage.get('options_advanced_edit_mode') != null
    ) {
      return $.jStorage.get('options_advanced_edit_mode')
    }
    return false
  })

  const closeDrawer = useCallback(
    event => {
      const closingId = activeDrawer
      document.body.classList.remove('has-chat-drawer')
      setActiveDrawer(null)

      const readerLayout = document.getElementById('reader_layout')
      const arrow = document.querySelector('.navigation_arrow_right')
      if (readerLayout) {
        readerLayout.style.marginRight = ''
      }
      if (arrow) {
        arrow.style.right = ''
      }

      const fromCoordinator =
        event && event.detail && event.detail.fromCoordinator

      try {
        const courseNum = props.coursenum
        if (courseNum && !fromCoordinator) {
          localStorage.setItem(`chatDisplayWin-${courseNum}`, 'false')
          localStorage.removeItem(`chatActiveDrawer-${courseNum}`)
        }
      } catch (e) {
        console.warn('Failed to update drawer state in localStorage', e)
      }

      // Return focus to the icon that opened this drawer, unless it was
      // closed indirectly (e.g. PanelCoordinator making room for the other side)
      // The button lives in the iconbar, not inside the drawer's animating
      // subtree, so we only need to wait for layout to settle after the
      // synchronous DOM changes above — not for the drawer's close
      // transition to finish.
      if (!fromCoordinator && closingId) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const button = document.getElementById(closingId)
            if (button) button.focus()
          })
        })
      }
    },
    [props.coursenum, activeDrawer]
  )

  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return

    const bodyObserver = new MutationObserver(() => {
      const isOpen = document.body.classList.contains('has-chat-drawer')
      setAssistantOpen(isOpen)
      if (!isOpen) {
        setActiveDrawer(null)
      }
    })
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => bodyObserver.disconnect()
  }, [])

  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return

    const dialogObserver = new MutationObserver(() => {
      setGlossaryOpen(!!document.getElementById('dialog_view_glossary'))
      setSettingsOpen(!!document.getElementById('dialog_advanced_options'))
    })
    dialogObserver.observe(document.body, { childList: true, subtree: true })
    return () => dialogObserver.disconnect()
  }, [])

  useEffect(() => {
    try {
      const key = `chatActiveDrawer-${props.coursenum}`
      const stored = localStorage.getItem(key)
      const chatDisplayKey = `chatDisplayWin-${props.coursenum}`
      const chatDisplayStored = localStorage.getItem(chatDisplayKey)

      if (stored && chatDisplayStored === 'true') {
        if (isDrawerDisabled(stored)) {
          setActiveDrawer(null)
          localStorage.setItem(chatDisplayKey, 'false')
          localStorage.removeItem(key)
          document.body.classList.remove('has-chat-drawer')

          const readerLayout = document.getElementById('reader_layout')
          const arrow = document.querySelector('.navigation_arrow_right')
          if (readerLayout) {
            readerLayout.style.marginRight = ''
          }
          if (arrow) {
            arrow.style.right = ''
          }
        } else {
          setActiveDrawer(stored)
          if (!document.body.classList.contains('has-chat-drawer')) {
            document.body.classList.add('has-chat-drawer')
          }
          setTimeout(updateLayoutMargins, 100)
          setTimeout(() => {
            const event = new CustomEvent('drawer-state-change', {
              detail: { activeDrawer: stored },
            })
            document.dispatchEvent(event)
          }, 200)
        }
      } else if (chatDisplayStored === 'false') {
        setActiveDrawer(null)
        document.body.classList.remove('has-chat-drawer')

        const readerLayout = document.getElementById('reader_layout')
        const arrow = document.querySelector('.navigation_arrow_right')
        if (readerLayout) {
          readerLayout.style.marginRight = ''
        }
        if (arrow) {
          arrow.style.right = ''
        }
      }
    } catch (e) {
      console.warn('Failed to restore drawer state from localStorage', e)
    }
  }, [props.coursenum, props.assistantEnabled])

  useEffect(() => {
    try {
      const key = `chatActiveDrawer-${props.coursenum}`
      if (activeDrawer) {
        localStorage.setItem(key, activeDrawer)
      } else {
        // keep last value so we can restore on next open; do not remove
      }
    } catch (e) {
      console.warn('Failed to save active drawer to localStorage', e)
    }
  }, [activeDrawer, props.coursenum])

  const toggleDrawer = id => {
    if (isDrawerDisabled(id)) {
      console.warn(`The ${id} drawer is not available here`)
      return
    }

    if (activeDrawer === id) {
      closeDrawer()
    } else {
      if (!document.body.classList.contains('has-chat-drawer')) {
        document.body.classList.add('has-chat-drawer')
      }
      setActiveDrawer(id)
      try {
        const courseNum = props.coursenum
        if (courseNum)
          localStorage.setItem(`chatDisplayWin-${courseNum}`, 'true')
      } catch (e) {
        console.warn('Failed to save drawer display state to localStorage', e)
      }
    }

    updateLayoutMargins()
  }

  const updateLayoutMargins = () => {
    const readerLayout = document.getElementById('reader_layout')
    const iconbar = document.getElementById('reader_iconbar')
    const drawer = document.getElementById('chatbot_drawer')

    if (readerLayout && iconbar && drawer) {
      const iconbarWidth = iconbar.offsetWidth
      const drawerWidth = drawer.offsetWidth

      const arrow = document.querySelector('.navigation_arrow_right')

      if (document.body.classList.contains('has-chat-drawer')) {
        readerLayout.style.marginRight = `${drawerWidth + iconbarWidth}px`
        if (arrow) arrow.style.right = `${drawerWidth + iconbarWidth + 30}px`
      } else {
        readerLayout.style.marginRight = ''
        if (arrow) arrow.style.right = ''
      }
    }
  }

  useEffect(() => {
    const drawerElement = document.getElementById('chatbot_drawer')
    if (
      drawerElement &&
      !drawerElement.querySelector('.drawer-content-container')
    ) {
      const drawerContentElement = document.createElement('div')
      drawerContentElement.className = 'drawer-content-container'
      drawerContentElement.style.height = '100%'
      drawerContentElement.style.width = '100%'
      drawerContentElement.id = 'reader_drawers'
      drawerElement.appendChild(drawerContentElement)
    }
  }, [])

  useEffect(() => {
    const event = new CustomEvent('drawer-state-change', {
      detail: { activeDrawer },
    })
    document.dispatchEvent(event)
  }, [activeDrawer])

  useEffect(() => {
    const onDrawerStateChange = event => {
      const id = event && event.detail && event.detail.activeDrawer
      if (!id) return
      if (isDrawerDisabled(id)) return

      if (!document.body.classList.contains('has-chat-drawer')) {
        document.body.classList.add('has-chat-drawer')
      }
      setActiveDrawer(id)
      try {
        if (props.coursenum)
          localStorage.setItem(`chatDisplayWin-${props.coursenum}`, 'true')
        if (props.coursenum)
          localStorage.setItem(`chatActiveDrawer-${props.coursenum}`, id)
      } catch (e) {
        console.warn('Failed to save drawer state to localStorage', e)
      }
      updateLayoutMargins()
    }

    const onAIChatOpenDrawer = () => {
      if (props.assistantEnabled === false) {
        return
      }

      if (!document.body.classList.contains('has-chat-drawer')) {
        document.body.classList.add('has-chat-drawer')
      }
      setActiveDrawer('assistant')
      try {
        if (props.coursenum)
          localStorage.setItem(`chatDisplayWin-${props.coursenum}`, 'true')
        if (props.coursenum)
          localStorage.setItem(
            `chatActiveDrawer-${props.coursenum}`,
            'assistant'
          )
      } catch (e) {
        console.warn('Failed to set chat display state', e)
      }
      updateLayoutMargins()
    }

    document.addEventListener('drawer-state-change', onDrawerStateChange)
    window.addEventListener('aichat-open-drawer', onAIChatOpenDrawer)
    window.addEventListener('close-chat-drawer', closeDrawer)
    return () => {
      document.removeEventListener('drawer-state-change', onDrawerStateChange)
      window.removeEventListener('aichat-open-drawer', onAIChatOpenDrawer)
      window.removeEventListener('close-chat-drawer', closeDrawer)
    }
  }, [props.coursenum, props.assistantEnabled, closeDrawer])

  useEffect(() => {
    const eventsRef = { elem: null }

    const updateVisible = () => {
      const elem = eventsRef.elem
      setNarrateVisible(
        !!(elem && elem.provider && elem.provider.playerVisible)
      )
    }

    const onHidePlayer = () => {
      if (eventsRef.elem) eventsRef.elem.removeAttribute('show')
      setNarrateVisible(false)
    }

    const setup = elem => {
      eventsRef.elem = elem
      setNarrateElem(elem)
      updateVisible()
      elem.addEventListener('vapp-ready', updateVisible)
      elem.addEventListener('vapp-shutdown', updateVisible)
      elem.addEventListener('vapp-show-player', updateVisible)
      elem.addEventListener('vapp-hide-player', onHidePlayer)
    }

    const existing = document.querySelector('vapp-narrate')
    if (existing && existing.provider) {
      setup(existing)
    }

    const monitor = monitorNarrateElementReadiness(elem => {
      if (!eventsRef.elem) setup(elem)
    })

    return () => {
      monitor.stop()
      if (eventsRef.elem) {
        eventsRef.elem.removeEventListener('vapp-ready', updateVisible)
        eventsRef.elem.removeEventListener('vapp-shutdown', updateVisible)
        eventsRef.elem.removeEventListener('vapp-show-player', updateVisible)
        eventsRef.elem.removeEventListener('vapp-hide-player', onHidePlayer)
      }
    }
  }, [])

  const handleHighlightToggle = () => {
    const newState = !highlightOn
    setHighlightOn(newState)
    if (typeof window.setHighlightMode === 'function') {
      window.setHighlightMode(newState)
    }
    if (typeof window.resetHighlights === 'function') {
      window.resetHighlights()
    }
  }

  const handleNarrateToggle = () => {
    if (!narrateElem) return
    const newVisible = !narrateVisible
    if (newVisible) {
      narrateElem.setAttribute('show', 'show')
    } else {
      narrateElem.removeAttribute('show')
    }
    setNarrateVisible(newVisible)
  }

  const handleAuthorToolToggle = () => {
    const newState = !authorToolOn
    setAuthorToolOn(newState)
    if (typeof window.setAdvancedEditMode === 'function') {
      window.setAdvancedEditMode(newState)
    }
  }

  const getAdvancedEditModeLabel = () => {
    if (props.userRole === 'author') return formatMessage('Author Tools')
    if (props.userRole === 'editor') return formatMessage('Editor Tools')
    return formatMessage('Instructor Tools')
  }

  const isIconActive = id => activeDrawer === id

  const handleRequestAccess = () => {
    if (typeof window !== 'undefined' && window.$ && window.$.meDialog) {
      const dialogConfig = {
        id: 'request_access_dialog',
        url: `/reader/web.request_access/${props.coursenum}/`,
        width: 800,
        title: 'Request Access',
      }
      window.$.meDialog.open2(dialogConfig)
    }
  }

  const demoAccessItem = {
    id: 'request-access',
    ariaLabel: 'Request Full Access',
    tooltip: (
      <Tooltip id="iconbar_request_access_tt">
        {formatMessage('Request Full Access')}
      </Tooltip>
    ),
    iconClass: 'fas fa-key',
    onClick: handleRequestAccess,
    isSpecial: true,
  }

  const topItems = [
    ...(props.demoAccess ? [demoAccessItem] : []),
    {
      id: 'quickreview',
      ariaLabel: 'Quick Review',
      tooltip: (
        <Tooltip id="iconbar_quickreview_tt">
          {formatMessage('Quick Review')}
        </Tooltip>
      ),
      iconClass: 'fa-light fa-bolt',
      onClick: () => toggleDrawer('quickreview'),
      // on the title page the course's AI tools stay visible but disabled
      hidden: !(
        props.assistantEnabled ||
        (isCoverpage && props.courseAssistantEnabled)
      ),
      disabled: isDrawerDisabled('quickreview'),
    },
    {
      id: 'assistant',
      ariaLabel: '',
      tooltip: (
        <Tooltip id="iconbar_assistant_tt">
          {formatMessage('Virtual Assistant')}
        </Tooltip>
      ),
      iconClass: 'fal fa-robot',
      onClick: () => toggleDrawer('assistant'),
      hidden: !(
        props.assistantEnabled ||
        (isCoverpage && props.courseAssistantEnabled)
      ),
      disabled: isDrawerDisabled('assistant'),
    },
    {
      id: 'search',
      ariaLabel: '',
      tooltip: (
        <Tooltip id="iconbar_search_tt">{formatMessage('Search')}</Tooltip>
      ),
      iconClass: 'fa-light fa-search',
      onClick: () => toggleDrawer('search'),
    },
    {
      id: 'glossary',
      ariaLabel: '',
      tooltip: (
        <Tooltip id="iconbar_glossary_tt">{formatMessage('Glossary')}</Tooltip>
      ),
      iconClass: 'fa-light fa-book-font',
      onClick: () => toggleDrawer('glossary'),
    },
    {
      id: 'flashcards',
      ariaLabel: 'Flashcards',
      tooltip: (
        <Tooltip id="iconbar_flashcards_tt">
          {hasUser
            ? formatMessage('Flashcards (opens in new page)')
            : formatMessage('Please login to use this feature')}
        </Tooltip>
      ),
      iconClass: 'fa-light fa-cards-blank',
      isLink: true,
      href: hasUser ? `/reader/flashcards/${props.coursenum}/` : undefined,
      disabled: !hasUser,
    },
    {
      id: 'notebook',
      ariaLabel: 'Notebook',
      tooltip: (
        <Tooltip id="iconbar_notebook_tt">{formatMessage('Notebook')}</Tooltip>
      ),
      iconClass: 'fa-light fa-pen-to-square',
      onClick: () => toggleDrawer('notebook'),
      hidden: !props.allowNotebook,
      disabled: isDrawerDisabled('notebook'),
    },
  ]

  const middleItems = [
    {
      id: 'highlighting',
      ariaLabel: 'Highlighting',
      tooltip: (
        <Tooltip id="iconbar_highlighting_tt">
          {formatMessage('Highlighting')}
        </Tooltip>
      ),
      iconClass: 'fa-light fa-highlighter',
      activeIconClass: 'fa-kit fa-light-highlighter-circle-check-tl',
      onClick: handleHighlightToggle,
      active: highlightOn && !isDrawerDisabled('highlighting'),
      disabled: isDrawerDisabled('highlighting'),
    },
    {
      id: 'narration',
      ariaLabel: 'Narration',
      tooltip: (
        <Tooltip id="iconbar_narration_tt">
          {formatMessage('Narration')}
        </Tooltip>
      ),
      iconClass: 'fa-light fa-headphones',
      activeIconClass: 'fa-kit fa-light-headphones-circle-check-tl',
      onClick: handleNarrateToggle,
      active: narrateVisible,
      hidden: !narrateElem,
    },
    {
      id: 'author-tools',
      ariaLabel: getAdvancedEditModeLabel(),
      tooltip: (
        <Tooltip id="iconbar_author_tools_tt">
          {getAdvancedEditModeLabel()}
        </Tooltip>
      ),
      iconClass: 'fa-light fa-pen-nib',
      activeIconClass: 'fa-kit fa-light-pen-nib-circle-check-tl',
      onClick: handleAuthorToolToggle,
      active: authorToolOn,
      hidden: !props.allowAuthorTools,
    },
  ]

  const bottomItems = [
    {
      id: 'settings',
      ariaLabel: 'Settings',
      tooltip: (
        <Tooltip id="iconbar_settings_tt">{formatMessage('Settings')}</Tooltip>
      ),
      iconClass: 'fal fa-cog',
      onClick: () => toggleDrawer('settings'),
    },
    {
      id: 'contact',
      ariaLabel: '',
      tooltip: (
        <Tooltip id="iconbar_contact_tt">{formatMessage('Contact Us')}</Tooltip>
      ),
      iconClass: 'fa-light fa-envelope',
      onClick: () => toggleDrawer('contact'),
    },
    {
      id: 'help',
      ariaLabel: 'Help',
      tooltip: <Tooltip id="iconbar_help_tt">{formatMessage('Help')}</Tooltip>,
      iconClass: 'fa-light fa-question-circle',
      isLink: true,
      href: 'https://myeducator.freshdesk.com/support/home',
      target: '_blank',
    },
  ]

  // Disabled items keep the `disabled` class rather than the attribute: a disabled <button>
  // swallows mouse events, so the tooltip explaining why would never show.
  const tooltipFor = item =>
    item.disabled && isCoverpage ? (
      <Tooltip id={`iconbar_${item.id}_tt`}>{disabledTooltip}</Tooltip>
    ) : (
      item.tooltip
    )

  const renderIconItem = (item, isActive) => {
    if (item.hidden) return null
    const ariaLabel = item.ariaLabel || item.id
    const baseClass = `reader-icon-btn${isActive ? ' active' : ''}${
      item.isSpecial ? ' request-access-btn' : ''
    }${item.disabled ? ' disabled' : ''}`

    const inner = <div className={`fa-fw ${item.iconClass}`} />

    const component = item.isLink ? (
      <a
        key={item.id}
        href={item.href}
        target={item.target}
        rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
        aria-label={ariaLabel}
        aria-disabled={item.disabled || undefined}
        id={item.id}
        className={`${baseClass} btn btn-link btn-sm`}
        onClick={e => {
          if (item.disabled) {
            e.preventDefault()
            return
          }
          setActiveIcon(item.id)
        }}
      >
        {inner}
      </a>
    ) : (
      <Button
        key={item.id}
        variant="link"
        size="sm"
        aria-label={ariaLabel}
        aria-disabled={item.disabled || undefined}
        id={item.id}
        className={baseClass}
        onClick={() => {
          if (item.disabled) return
          setActiveIcon(item.id)
          item.onClick && item.onClick()
        }}
      >
        {inner}
      </Button>
    )

    return (
      <OverlayTrigger key={item.id} placement="left" overlay={tooltipFor(item)}>
        {component}
      </OverlayTrigger>
    )
  }

  return (
    <div id="reader_iconbar_inner" className="reader-iconbar-inner">
      <div className="iconbar-top-section">
        {topItems.map(item => renderIconItem(item, isIconActive(item.id)))}
      </div>

      <div className="iconbar-divider"></div>

      <div className="iconbar-middle-section">
        {middleItems.map(item => {
          if (item.hidden) return null
          const ariaLabel = item.ariaLabel || item.id
          return (
            <OverlayTrigger
              key={item.id}
              placement="left"
              overlay={tooltipFor(item)}
            >
              <Button
                variant="link"
                size="sm"
                aria-label={ariaLabel}
                aria-disabled={item.disabled || undefined}
                id={item.id}
                className={`reader-icon-btn${
                  item.active ? ' toggle-active' : ''
                }${item.disabled ? ' disabled' : ''}`}
                onClick={() => {
                  if (item.disabled) return
                  setActiveIcon(item.id)
                  item.onClick && item.onClick()
                }}
              >
                <i
                  className={`${
                    item.active && item.activeIconClass
                      ? item.activeIconClass
                      : item.iconClass
                  }`}
                />
              </Button>
            </OverlayTrigger>
          )
        })}
      </div>

      <div className="iconbar-bottom-section">
        {bottomItems.map(item => renderIconItem(item, isIconActive(item.id)))}
      </div>
    </div>
  )
}

IconBar.propTypes = {
  coursenum: PropTypes.string.isRequired,
  pageId: PropTypes.string,
  userId: PropTypes.string,
  userEmail: PropTypes.string,
  allowNotebook: PropTypes.bool.isRequired,
  allowAuthorTools: PropTypes.bool,
  userRole: PropTypes.string,
  glossaryFunc: PropTypes.func,
  assistantFunc: PropTypes.func,
  assistantEnabled: PropTypes.bool,
  advancedOptionsFunc: PropTypes.func,
  demoAccess: PropTypes.bool,
  // 'reader' (default) or 'coverpage': the title page greys out page-bound tools
  context: PropTypes.oneOf(['reader', 'coverpage']),
  // whether the course has AI tools at all (assistantEnabled says whether the drawers are usable here)
  courseAssistantEnabled: PropTypes.bool,
}
